/** @jsx jsx */
import { css, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { NumericStats } from '../../../common/types.tsx';
import messages from './messages.tsx';

type Props = {
	data: NumericStats;
};

const tableStyles = css({
	borderCollapse: 'separate',
	borderSpacing: 0,
	borderWidth: '1px',
	borderStyle: 'solid',
	borderColor: `${token('color.border')}`,
	borderRadius: '3px',
});

const cellStyles = css({
	paddingTop: token('space.150'),
	paddingBottom: token('space.150'),
});

// This is required to override the css reset which sets padding-left to 0.
const statisticStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&:nth-of-type(n + 1)': {
		paddingLeft: token('space.200'),
	},
});

const valueStyles = css({
	borderLeft: `1px solid ${token('color.border')}`,
	paddingLeft: token('space.200'),
});

const grayBackgroundStyles = css({
	backgroundColor: token('color.background.accent.gray.subtlest'),
});

export const TwoCellRow = ({
	left,
	right,
	rowNumber,
}: {
	left: string;
	right: string | number | null;
	rowNumber: number;
}) => {
	const Tag = rowNumber === 0 ? 'th' : 'td';
	const isGrayBackground = rowNumber % 2 === 1;

	return (
		<tr>
			<Tag css={[cellStyles, statisticStyles, isGrayBackground && grayBackgroundStyles]}>
				{left}
			</Tag>
			<Tag css={[cellStyles, valueStyles, isGrayBackground && grayBackgroundStyles]}>{right}</Tag>
		</tr>
	);
};

export const NumericStatsTable = ({ data }: Props) => {
	const { formatMessage } = useIntl();

	return (
		<table css={tableStyles}>
			<thead>
				<TwoCellRow
					left={formatMessage(messages.statistic)}
					right={formatMessage(messages.value)}
					rowNumber={0}
				/>
			</thead>
			<tbody>
				<TwoCellRow left={formatMessage(messages.maximum)} right={data.max} rowNumber={1} />
				<TwoCellRow left={formatMessage(messages.minimum)} right={data.min} rowNumber={2} />
				<TwoCellRow left={formatMessage(messages.sum)} right={data.sum} rowNumber={3} />
				<TwoCellRow left={formatMessage(messages.count)} right={data.count} rowNumber={4} />
			</tbody>
		</table>
	);
};
